import { useCallback, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { LuBell, LuBellRing } from "react-icons/lu";
import useDismiss, { DismissReason } from "../../hooks/useDismiss";
import { formatDate } from "../../utils";

export type AdminNotification = {
  id: string | number;
  title: string;
  body?: string;
  created_at: string;
  read: boolean;
  href?: string;
};

type NotificationBellProps = {
  notifications?: AdminNotification[];
  onMarkAllRead?: () => void;
};

/**
 * Bell + dropdown panel. Shows whatever notifications it is given; the header
 * passes an empty list until a backend notifications endpoint exists.
 */
const NotificationBell = ({ notifications = [], onMarkAllRead }: NotificationBellProps) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const unread = notifications.filter((n) => !n.read).length;
  const badge = unread > 9 ? "9+" : String(unread);

  const dismiss = useCallback((reason: DismissReason) => {
    setOpen(false);
    if (reason === "escape") buttonRef.current?.focus();
  }, []);
  useDismiss(wrapRef, open, dismiss);

  return (
    <div ref={wrapRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={unread ? `Notifications, ${unread} unread` : "Notifications"}
        className={`relative flex h-10 w-10 items-center justify-center rounded-full text-honeConnectGray transition-colors hover:bg-hcBg hover:text-honeConnectBlack focus:outline-none focus-visible:ring-2 focus-visible:ring-honeConnectPurple/40 ${
          open ? "bg-hcBg text-honeConnectBlack" : ""
        }`}
      >
        <LuBell className="h-5 w-5" aria-hidden />
        {unread > 0 && (
          <span className="absolute right-1 top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-honeConnectOrange px-1 text-[10px] font-semibold leading-none text-white ring-2 ring-white">
            {badge}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Notifications"
          className="fixed inset-x-4 top-[72px] z-30 overflow-hidden rounded-xl border border-hcBorder bg-white shadow-pop sm:absolute sm:inset-x-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-96"
        >
          <div className="flex items-center justify-between border-b border-hcBorder px-4 py-3">
            <p className="text-sm font-semibold text-honeConnectBlack">Notifications</p>
            {onMarkAllRead && (
              <button
                type="button"
                onClick={onMarkAllRead}
                disabled={!unread}
                className="text-xs font-medium text-honeConnectPurple hover:underline disabled:cursor-not-allowed disabled:text-hcMuted disabled:no-underline"
              >
                Mark all as read
              </button>
            )}
          </div>

          {notifications.length === 0 ? (
            <div className="flex flex-col items-center px-6 py-10 text-center">
              <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-hc_F4EBFA text-honeConnectPurple">
                <LuBellRing className="h-5 w-5" aria-hidden />
              </div>
              <p className="text-sm font-semibold text-honeConnectBlack">
                No notifications yet
              </p>
              <p className="mt-1 max-w-[260px] text-xs leading-relaxed text-hcMuted">
                Alerts for the admin team will show up here.
              </p>
            </div>
          ) : (
            <ul className="max-h-[60vh] divide-y divide-hcBorder overflow-y-auto">
              {notifications.map((n) => {
                const content = (
                  <div className="flex gap-3 px-4 py-3">
                    <span
                      aria-hidden
                      className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                        n.read ? "bg-transparent" : "bg-honeConnectPurple"
                      }`}
                    />
                    <div className="min-w-0">
                      <p
                        className={`text-sm ${
                          n.read ? "text-honeConnectGray" : "font-medium text-honeConnectBlack"
                        }`}
                      >
                        {n.title}
                      </p>
                      {n.body && (
                        <p className="mt-0.5 line-clamp-2 text-xs text-hcMuted">{n.body}</p>
                      )}
                      <p className="mt-1 text-[11px] text-hcMuted">
                        {formatDate(n.created_at)}
                      </p>
                    </div>
                  </div>
                );
                return (
                  <li key={n.id} className="hover:bg-hcBg">
                    {n.href ? (
                      <Link to={n.href} onClick={() => setOpen(false)} className="block">
                        {content}
                      </Link>
                    ) : (
                      content
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
